import { useContext, useMemo, useState } from 'react';
import { format as formatFNS, isToday as isTodayFNS } from 'date-fns';
import * as R from 'ramda';

import CreateWorkModal from '@atom/components/common/createWorkModals/CreateWorkModal';
import { usePreferences } from '@atom/hooks/usePreferences';
import { CalendarView } from '@atom/hooks/useWorkOrdersFilters';
import { Icon, Tooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { AppointmentsMap } from '@atom/types/appointments';

import WorkOrdersContext from '../WorkOrdersContext';

import { getCalendarDateKey } from './WorkOrdersCalendar';
import WorkOrdersCalendarEvent from './WorkOrdersCalendarEvent';

interface Props {
  children: any;
  value: Date;
  appointmentsMap: AppointmentsMap;
}

const dayCellWidth = 100 / 7;

const styles = {
  container: {
    position: 'relative',
    backgroundColor: colors.neutral.white,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${dayCellWidth}%`,
  },
  event: {
    fontSize: fonts.sm,
    color: colors.neutral.dim,
    borderRadius: '4px',
    height: '18px',
    padding: '0 4px',
    margin: '1px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  eventHeader: {
    height: '20px',
    textAlign: 'center',
  },
  eventFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    background: colors.neutral.white,
    fontSize: fonts.sm,
  },
  today: {
    backgroundColor: colors.brand.blue,
    color: colors.neutral.white,
    borderRadius: '1.15rem',
    padding: '0 0.25rem',
    fontSize: fonts.sm,
    cursor: 'pointer',
    fontWeight: 500,
  },
};

const WorkOrdersCalendarCell = (props: Props) => {
  const preferences = usePreferences();
  const requireWorkType = R.pathOr(
    false,
    ['workOrders', 'requireWorkType'],
    preferences,
  );

  const { children, value, appointmentsMap } = props;
  const { input, setInput } = useContext(WorkOrdersContext);

  const [hovering, setHovering] = useState(false);
  const [workModalOpen, setWorkModalOpen] = useState(false);

  const events = R.pathOr(
    [],
    [getCalendarDateKey(value), 'workOrders'],
    appointmentsMap,
  );
  const count: number = R.pathOr(
    0,
    [getCalendarDateKey(value), 'totalWorkOrderCount'],
    appointmentsMap,
  );

  const moreCount = count - events.length;

  const dayOfMonth: string = formatFNS(value, 'dd');

  const isToday = isTodayFNS(value);
  const dayPrefix =
    dayOfMonth === '01' ? formatFNS(value, 'MMM').toUpperCase() : '';

  const iconStyle = useMemo(
    () => ({
      visibility: hovering ? 'visible' : 'hidden',
      cursor: 'pointer',
    }),
    [hovering],
  );
  const dayStyle = isToday ? styles.today : { cursor: 'pointer' };

  return (
    <>
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={children.props.className}
        style={styles.container}
      >
        <div style={styles.eventHeader}>
          <span
            onClick={() =>
              setInput({
                ...input,
                calendarView: CalendarView.WEEK,
                calendarViewDate: value.valueOf(),
              })
            }
            style={dayStyle}
          >
            {dayPrefix} {dayOfMonth}
          </span>
        </div>
        {events.map(workOrder => (
          <WorkOrdersCalendarEvent workOrder={workOrder} />
        ))}
        <div style={styles.eventFooter}>
          <div
            onClick={() =>
              setInput({
                ...input,
                calendarView: CalendarView.WEEK,
                calendarViewDate: value.valueOf(),
              })
            }
            style={{
              visibility: moreCount > 0 ? 'visible' : 'hidden',
              cursor: 'pointer',
            }}
          >
            + {moreCount} more
          </div>
          <Tooltip title="Create Work">
            <div
              onClick={() => setWorkModalOpen(true)}
              // @ts-ignore
              style={iconStyle}
            >
              <Icon>add</Icon>
            </div>
          </Tooltip>
        </div>
      </div>
      <CreateWorkModal
        fromCalendarDate={value}
        open={workModalOpen}
        onClose={() => setWorkModalOpen(false)}
        workTemplateRequired={requireWorkType}
        redirectOnCreate
      />
    </>
  );
};

export default WorkOrdersCalendarCell;
